import { useState, useRef, useEffect } from 'react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { BACKEND_CONFIG } from '../utils/productionConfig';
import { useAuthContext } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Heart, ArrowLeft, Phone, Mail, MapPin, Briefcase, GraduationCap, Home, Users, Calendar, Shield, Star, ChevronLeft, ChevronRight, Lock, Eye, MessageCircle, Share2, MoreVertical, Clock, Check, X, Loader2 } from 'lucide-react';
import BuyPlanModal from './BuyPlanModal';

const API_URL = BACKEND_CONFIG.apiUrl;

const heightToFeetInches = (cm: number): string => {
  if (!cm || typeof cm !== 'number') return "5'5\"";
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}'${inches}"`;
};

interface Profile {
  id: string;
  _id?: string;
  userId?: any;
  name: string;
  age: number;
  height: string;
  location: string;
  education: string;
  occupation: string;
  company: string;
  income: string;
  religion: string;
  community?: string;
  caste?: string;
  image: string;
  isPro: boolean;
  isOnline: boolean;
  profileManaged: string;
  lastActive: string;
  about?: string;
  photos?: string[];
  isSponsored?: boolean;
  rawProfile?: any;
  dateOfBirth?: string;
  motherTongue?: string;
  maritalStatus?: string;
  familyType?: string;
  fatherOccupation?: string;
  motherOccupation?: string;
  siblings?: string;
  diet?: string;
  smoking?: string;
  drinking?: string;
}

interface ViewProfilePageProps {
  profile: Profile;
  onBack: () => void;
  hideInterestButtons?: boolean;
  interestContext?: 'received' | 'sent' | 'accepted' | null;
  onNavigateToPlans?: () => void;
  onViewProfile?: (profile: any) => void;
}

export default function ViewProfilePage({
  profile,
  onBack,
  hideInterestButtons,
  onNavigateToPlans,
  onViewProfile,
}: ViewProfilePageProps) {
  const { user: authUser } = useAuthContext();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [revealingContact, setRevealingContact] = useState(false);
  const [contactInfo, setContactInfo] = useState<any>(null);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [receivedInteraction, setReceivedInteraction] = useState<any>(null);
  const [acceptingInterest, setAcceptingInterest] = useState(false);
  const [recentlyActive, setRecentlyActive] = useState<any[]>([]);
  const [similarProfiles, setSimilarProfiles] = useState<any[]>([]);
  const [declinedInteraction, setDeclinedInteraction] = useState<any>(null);

  // Buy Plan Modal state
  const [showBuyPlanModal, setShowBuyPlanModal] = useState(false);
  const [buyPlanError, setBuyPlanError] = useState<string>('');

  // Extract photos
  let photos: string[] = [];

  // Try multiple sources for photos
  if (profile.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
    photos = profile.photos
      .filter((p: any) => {
        if (!p) return false;
        if (typeof p === 'object') {
          return p.status === 'approved' || p.isApproved === true;
        }
        return true;
      })
      .map((p: any) => {
        if (typeof p === 'string' && p.trim() !== '') return p;
        if (p && typeof p === 'object' && p.url && typeof p.url === 'string' && p.url.trim() !== '') return p.url;
        return null;
      })
      .filter((url: any): url is string => url !== null);
  } else if (profile.rawProfile?.photos && Array.isArray(profile.rawProfile.photos) && profile.rawProfile.photos.length > 0) {
    // Try rawProfile.photos
    photos = profile.rawProfile.photos
      .map((p: any) => {
        if (typeof p === 'string' && p.trim() !== '') return p;
        if (p && typeof p === 'object' && p.url && typeof p.url === 'string' && p.url.trim() !== '') return p.url;
        return null;
      })
      .filter((url: any): url is string => url !== null);
  } else if (profile.image && typeof profile.image === 'string' && profile.image.trim() !== '') {
    photos = [profile.image];
  } else if (profile.rawProfile?.profilePhoto && typeof profile.rawProfile.profilePhoto === 'string' && profile.rawProfile.profilePhoto.trim() !== '') {
    photos = [profile.rawProfile.profilePhoto];
  } else if (profile.rawProfile?.userId?.profilePhoto && typeof profile.rawProfile.userId.profilePhoto === 'string' && profile.rawProfile.userId.profilePhoto.trim() !== '') {
    photos = [profile.rawProfile.userId.profilePhoto];
  }

  if (photos.length === 0) {
    photos = [""];
  }

  // Detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch recently active profiles
  useEffect(() => {
    const fetchRecentlyActive = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const axios = (await import('axios')).default;
        const response = await axios.get(`${API_URL}/profiles?filterType=recently-active&limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.data?.profiles) {
          setRecentlyActive(response.data.data.profiles.slice(0, 5));
        }
      } catch (error) {
        // Silent fail
      }
    };

    if (!isMobile) {
      fetchRecentlyActive();
    }
  }, [isMobile]);

  // Fetch similar profiles
  useEffect(() => {
    const fetchSimilarProfiles = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const axios = (await import('axios')).default;
        const response = await axios.get(`${API_URL}/profiles?limit=8`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.data?.profiles) {
          setSimilarProfiles(response.data.data.profiles.slice(0, 8));
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchSimilarProfiles();
  }, []);

  // Fetch subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const axios = (await import('axios')).default;
        const response = await axios.get(`${API_URL}/user/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setUserSubscription(response.data.data);
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchSubscription();
  }, []);

  // Check received interest
  useEffect(() => {
    const checkReceivedInterest = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const profileId = profile.rawProfile?._id || (profile as any)._id || profile.userId?._id || profile.id;
        if (!profileId) return;

        const axios = (await import('axios')).default;
        const response = await axios.get(`${API_URL}/interactions/received?status=pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          let interactions = [];
          if (response.data.data?.interactions) {
            interactions = response.data.data.interactions;
          } else if (Array.isArray(response.data.data)) {
            interactions = response.data.data;
          } else if (response.data.interactions) {
            interactions = response.data.interactions;
          }

          const interactionFromThisProfile = interactions.find((int: any) => {
            const fromProfileId = int.fromUserProfile?._id || int.fromProfile?._id;
            return fromProfileId === profileId && int.type === 'interest' && int.status === 'pending';
          });

          setReceivedInteraction(interactionFromThisProfile);
        }
      } catch (error) {
        // Silent fail
      }
    };

    checkReceivedInterest();
  }, [profile]);

  // Check if user already sent interest that was declined
  useEffect(() => {
    const checkDeclinedInteraction = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const profileId = profile.rawProfile?._id || (profile as any)._id || profile.userId?._id || profile.id;
        if (!profileId) return;

        const axios = (await import('axios')).default;
        const response = await axios.get(`${API_URL}/interactions/sent?type=interest,superlike&status=declined`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          let interactions = [];
          if (response.data.data?.interactions) {
            interactions = response.data.data.interactions;
          } else if (Array.isArray(response.data.data)) {
            interactions = response.data.data;
          } else if (response.data.interactions) {
            interactions = response.data.interactions;
          }

          const declinedToThisProfile = interactions.find((int: any) => {
            const toProfileId = int.toProfile?._id || int.toUserProfile?._id;
            return toProfileId === profileId && int.status === 'declined';
          });

          setDeclinedInteraction(declinedToThisProfile);
        }
      } catch (error) {
        // Silent fail
      }
    };

    checkDeclinedInteraction();
  }, [profile]);

  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send interest');
        setIsLiking(false);
        return;
      }

      const profileId = profile.rawProfile?._id || (profile as any)._id || profile.userId?._id || profile.id;

      console.log('🚀 [ViewProfilePage] Sending interest:', {
        profileId,
        profileKeys: Object.keys(profile),
        rawProfileId: profile.rawProfile?._id,
        profileId_direct: (profile as any)._id,
        userId: profile.userId?._id,
        id: profile.id,
        fullProfile: profile
      });

      if (!profileId) {
        console.error('❌ [ViewProfilePage] No profile ID found:', profile);
        toast.error('Invalid profile ID');
        setIsLiking(false);
        return;
      }

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'interest'
        })
      });

      const data = await response.json();

      console.log('📥 [ViewProfilePage] Interest response:', data);

      if (data.success) {
        if (data.data?.isMatch) {
          toast.success("It's a Match! 💕", {
            description: 'You can now start chatting!',
            duration: 5000
          });
        } else {
          toast.success('Interest sent successfully! 💌');
        }
        setInterestSent(true);
        setTimeout(() => onBack(), 1500);
      } else {
        console.error('❌ [ViewProfilePage] Failed to send interest:', data);
        toast.error(data.message || 'Failed to send interest');
      }
    } catch (error: any) {
      console.error('❌ [ViewProfilePage] Error sending interest:', error);
      toast.error('Failed to send interest');
    } finally {
      setIsLiking(false);
    }
  };

  const handleAcceptInterest = async () => {
    if (acceptingInterest || !receivedInteraction) return;
    setAcceptingInterest(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to accept interest');
        setAcceptingInterest(false);
        return;
      }

      const axios = (await import('axios')).default;
      await axios.post(
        `${API_URL}/interactions/accept/${receivedInteraction._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`You accepted ${profile.name.split(' ')[0]}'s interest!`);
      setReceivedInteraction(null);
      setTimeout(() => onBack(), 1000);
    } catch (error: any) {
      toast.error('Failed to accept interest. Please try again.');
    } finally {
      setAcceptingInterest(false);
    }
  };

  const handleShortlist = async () => {
    if (isShortlisting) return;
    setIsShortlisting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to shortlist profiles');
        setIsShortlisting(false);
        return;
      }

      const profileId = profile.rawProfile?._id || (profile as any)._id || profile.userId?._id || profile.id;

      if (!profileId) {
        toast.error('Profile ID not found');
        setIsShortlisting(false);
        return;
      }

      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          profileId,
          type: 'shortlist'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsShortlisted(!isShortlisted);
        toast.success(isShortlisted ? 'Removed from shortlist' : 'Added to shortlist!');
      } else {
        toast.error(data.message || 'Failed to update shortlist');
      }
    } catch (error: any) {
      toast.error('Failed to update shortlist');
    } finally {
      setIsShortlisting(false);
    }
  };

  const handleRevealContact = async () => {
    if (revealingContact) return;
    setRevealingContact(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view contact details');
        setRevealingContact(false);
        return;
      }

      const profileId = profile.rawProfile?._id || (profile as any)._id || profile.userId?._id || profile.id;

      if (!profileId) {
        toast.error('Profile ID not found');
        setRevealingContact(false);
        return;
      }

      const axios = (await import('axios')).default;

      // Add response type validation
      const response = await axios.get(
        `${API_URL}/contact-reveal/${profileId}/contact`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500 // Don't throw on 4xx errors
        }
      );

      // Check if response is valid JSON
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Server returned an invalid response. Please try again later.');
      }

      if (response.data.success) {
        setContactInfo(response.data.data);
        setContactRevealed(true);

        if (!response.data.data.alreadyRevealed) {
          const remainingViews = response.data.data.contactViewsRemaining || 0;
          toast.success(`Contact revealed! ${remainingViews} views remaining.`);

          setUserSubscription(prev => ({
            ...prev,
            subscription: {
              ...prev.subscription,
              contactViewsRemaining: remainingViews
            }
          }));
        } else {
          toast.success('Contact details retrieved');
        }
      } else {
        // Failed response - check if it's a subscription error (403)
        const errorMsg = response.data.message || 'Failed to reveal contact';

        if (response.status === 403) {
          // Show modal instead of toast for subscription errors
          setBuyPlanError(errorMsg);
          setShowBuyPlanModal(true);
        } else {
          // For other errors, still show toast
          toast.error(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('❌ [ViewProfilePage] Contact reveal error:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to reveal contact';
      const isSubscriptionError = error?.response?.status === 403;

      if (isSubscriptionError) {
        // Show modal instead of toast for subscription errors
        setBuyPlanError(errorMsg);
        setShowBuyPlanModal(true);
      } else {
        // For other errors, still show toast
        toast.error(errorMsg);
      }
    } finally {
      setRevealingContact(false);
    }
  };

  // MOBILE VIEW
  if (isMobile) {
    return (
      <>
        {/* Buy Plan Modal */}
        <BuyPlanModal
          isOpen={showBuyPlanModal}
          onClose={() => setShowBuyPlanModal(false)}
          onViewPlans={() => {
            if (onNavigateToPlans) {
              onNavigateToPlans();
            }
          }}
          errorMessage={buyPlanError}
          remainingViews={userSubscription?.subscription?.contactViewsRemaining || 0}
        />

        <div className="h-screen flex flex-col bg-[#f8f6f6]">
          <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }\n          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }\n        `}</style>

          {/* Mobile Header */}
          <div className="flex-shrink-0 fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
            <div className="flex items-center p-4 justify-between h-16 max-w-md mx-auto w-full">
              <button
                onClick={onBack}
                className="flex size-10 items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="size-6 text-[#181111]" />
              </button>
              <h2 className="text-[#181111] text-lg">Profile Details</h2>
              <button
                onClick={handleShortlist}
                disabled={isShortlisting}
                className="flex size-10 items-center justify-center rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {isShortlisting ? (
                  <Loader2 className="size-6 text-[#181111] animate-spin" />
                ) : (
                  <Star className={`size-6 ${isShortlisted ? 'fill-amber-400 text-amber-400' : 'text-[#181111]'}`} />
                )}
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto pt-16 pb-24">
            <div className="max-w-md mx-auto w-full">
              <MobileContent
                profile={profile}
                photos={photos}
                currentPhotoIndex={currentPhotoIndex}
                setCurrentPhotoIndex={setCurrentPhotoIndex}
                contactRevealed={contactRevealed}
                contactInfo={contactInfo}
                handleRevealContact={handleRevealContact}
                revealingContact={revealingContact}
                onNavigateToPlans={onNavigateToPlans}
                userSubscription={userSubscription}
                authUser={authUser}
                similarProfiles={similarProfiles}
                onViewProfile={onViewProfile}
              />
            </div>
          </div>

          {/* Fixed Footer */}
          {!hideInterestButtons && (
            <div className="flex-shrink-0 fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 z-50">
              <div className="max-w-md mx-auto flex gap-3">
                <button
                  onClick={onBack}
                  className="flex-1 h-12 flex items-center justify-center rounded-xl border-2 border-gray-200 bg-transparent text-[#181111] hover:bg-gray-50 transition-colors gap-2"
                >
                  <X className="size-5" />
                  Skip
                </button>
                <button
                  onClick={receivedInteraction ? handleAcceptInterest : handleLike}
                  disabled={receivedInteraction ? acceptingInterest : isLiking}
                  className={`flex-[2] h-12 flex items-center justify-center rounded-xl ${receivedInteraction ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-rose-500 to-amber-400 hover:from-rose-600 hover:to-amber-500'} text-white shadow-lg transition-colors gap-2 disabled:opacity-50`}
                >
                  {receivedInteraction ? (
                    <>
                      <Check className="size-5" />
                      {acceptingInterest ? 'Accepting...' : 'Accept Interest'}
                    </>
                  ) : (
                    <>
                      <Heart className="size-5" />
                      {isLiking ? 'Sending...' : 'Send Interest'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // DESKTOP VIEW
  return (
    <>
      {/* Buy Plan Modal */}
      <BuyPlanModal
        isOpen={showBuyPlanModal}
        onClose={() => setShowBuyPlanModal(false)}
        onViewPlans={() => {
          if (onNavigateToPlans) {
            onNavigateToPlans();
          }
        }}
        errorMessage={buyPlanError}
        remainingViews={userSubscription?.subscription?.contactViewsRemaining || 0}
      />

      <div className="min-h-screen bg-gradient-to-br from-rose-50/30 via-white to-amber-50/30">
        {/* Top Navigation Bar */}
        <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="size-5" />
                <span className="hidden sm:inline">Back to Matches</span>
              </button>

              <div className="flex items-center gap-3">
                <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <Share2 className="size-5 text-gray-600" />
                </button>
                <button
                  onClick={handleShortlist}
                  disabled={isShortlisting}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isShortlisting ? (
                    <Loader2 className="size-5 text-gray-600 animate-spin" />
                  ) : (
                    <Star className={`size-5 ${isShortlisted ? 'fill-amber-400 text-amber-400' : 'text-gray-600'}`} />
                  )}
                  <span className="hidden sm:inline">{isShortlisted ? 'Shortlisted' : 'Shortlist'}</span>
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content with Sidebars */}
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Sidebar - Recently Active */}
            <aside className="hidden lg:block lg:col-span-3 space-y-4">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm sticky top-24">
                <h3 className="flex items-center gap-2 mb-4">
                  <Clock className="size-5 text-rose-500" />
                  Recently Active
                </h3>
                <div className="space-y-3">
                  {recentlyActive.length > 0 ? recentlyActive.map((prof, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <div className="relative">
                        <ImageWithFallback
                          src={prof.photos?.[0] || prof.profilePhoto || `https://ui-avatars.com/api/?name=${prof.firstName}+${prof.lastName}&background=f43f5e&color=fff`}
                          alt={`${prof.firstName} ${prof.lastName}`}
                          className="size-12 rounded-full object-cover"
                        />
                        <div className="absolute bottom-0 right-0 size-3 bg-green-500 border-2 border-white rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{prof.firstName} {prof.lastName?.charAt(0)}.</p>
                        <p className="text-xs text-gray-500 truncate">{prof.age} yrs, {prof.city}</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
                  )}
                </div>
                <button className="w-full mt-4 py-2 text-sm text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                  View All
                </button>
              </div>
            </aside>

            {/* Center Content - Profile Details */}
            <main className="lg:col-span-6 space-y-6">
              <DesktopProfileContent
                profile={profile}
                photos={photos}
                currentPhotoIndex={currentPhotoIndex}
                setCurrentPhotoIndex={setCurrentPhotoIndex}
                contactRevealed={contactRevealed}
                contactInfo={contactInfo}
                handleRevealContact={handleRevealContact}
                revealingContact={revealingContact}
                onNavigateToPlans={onNavigateToPlans}
                hideInterestButtons={hideInterestButtons}
                handleLike={handleLike}
                isLiking={isLiking}
                receivedInteraction={receivedInteraction}
                handleAcceptInterest={handleAcceptInterest}
                acceptingInterest={acceptingInterest}
                declinedInteraction={declinedInteraction}
                onBack={onBack}
              />
            </main>

            {/* Right Sidebar - Quick Info */}
            <aside className="hidden lg:block lg:col-span-3 space-y-4">
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm sticky top-24">
                <h3 className="mb-4">Quick Info</h3>
                <div className="space-y-3">
                  <QuickInfoItem icon={Calendar} label="Age" value={`${profile.age} years`} />
                  <QuickInfoItem icon={MapPin} label="Location" value={profile.location} />
                  <QuickInfoItem icon={Briefcase} label="Occupation" value={profile.occupation} />
                  <QuickInfoItem icon={Shield} label="Religion" value={profile.religion} />
                  <QuickInfoItem icon={Home} label="Profile Managed By" value={profile.profileManaged} />
                  <QuickInfoItem icon={Clock} label="Last Active" value={profile.lastActive} />
                </div>
              </div>
            </aside>
          </div>

          {/* Bottom Horizontal Section - Similar Profiles */}
          <div className="mt-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3>Similar Profiles</h3>
                <button className="text-sm text-rose-500 hover:text-rose-600">View All</button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                {similarProfiles.map((prof, idx) => (
                  <div
                    key={idx}
                    className="group cursor-pointer"
                    onClick={() => {
                      const firstName = prof.userId?.firstName || prof.firstName || 'User';
                      const lastName = prof.userId?.lastName || prof.lastName || '';
                      const profileId = prof._id || prof.id;
                      const slug = `${firstName.toLowerCase()}-${lastName.toLowerCase()}-${profileId}`.replace(/\s+/g, '-');
                      window.location.href = `/app/profile/${slug}`;
                    }}
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-gray-100">
                      <ImageWithFallback
                        src={prof.photos?.[0] || prof.profilePhoto || `https://ui-avatars.com/api/?name=${prof.firstName}+${prof.lastName}&background=f43f5e&color=fff`}
                        alt={`${prof.firstName} ${prof.lastName}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {prof.isOnline && (
                        <div className="absolute top-2 right-2 size-3 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <p className="text-sm truncate">{prof.firstName}, {prof.age}</p>
                    <p className="text-xs text-gray-500 truncate">{prof.city}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Mobile Content Component
function MobileContent({ profile, photos, currentPhotoIndex, setCurrentPhotoIndex, contactRevealed, contactInfo, handleRevealContact, revealingContact, onNavigateToPlans, userSubscription, authUser, similarProfiles, onViewProfile }: any) {
  return (
    <div className="flex flex-col gap-2">
      {/* Photo Carousel */}
      <div className="relative w-full aspect-[4/5] bg-gray-100">
        <ImageWithFallback
          src={photos[currentPhotoIndex]}
          alt={profile.name}
          className="w-full h-full object-cover"
        />
        {photos.length > 1 && (
          <>
            <button
              onClick={() => setCurrentPhotoIndex((prev: number) => (prev === 0 ? photos.length - 1 : prev - 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 size-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="size-6" />
            </button>
            <button
              onClick={() => setCurrentPhotoIndex((prev: number) => (prev === photos.length - 1 ? 0 : prev + 1))}
              className="absolute right-4 top-1/2 -translate-y-1/2 size-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronRight className="size-6" />
            </button>
          </>
        )}
        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs flex items-center gap-1">
          <Eye className="size-3.5" />
          <span>{currentPhotoIndex + 1}/{photos.length}</span>
        </div>
        {profile.isOnline && (
          <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5">
            <div className="size-2 bg-white rounded-full animate-pulse"></div>
            Online Now
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className="text-xl mb-1">{profile.name}, {profile.age}</h1>
            <p className="text-gray-600 flex items-center gap-2">
              <MapPin className="size-4" />
              {profile.location}
            </p>
          </div>
          {profile.isPro && (
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1">
              <Star className="size-3 fill-white" />
              Premium
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <Briefcase className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Occupation</p>
              <p className="text-sm">{profile.occupation}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <GraduationCap className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Education</p>
              <p className="text-sm">{profile.education}</p>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      {profile.about && (
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="mb-2">About</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{profile.about}</p>
        </div>
      )}

      {/* Personal Details */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h3 className="mb-3">Personal Details</h3>
        <div className="space-y-2">
          <DetailRow label="Height" value={profile.height} />
          <DetailRow label="Religion" value={profile.religion} />
          <DetailRow label="Mother Tongue" value={profile.motherTongue || 'Not specified'} />
          <DetailRow label="Marital Status" value={profile.maritalStatus || 'Never Married'} />
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h3 className="mb-3 flex items-center gap-2">
          <Phone className="size-5 text-rose-500" />
          Contact Details
        </h3>
        {contactRevealed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Phone className="size-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Phone Number</p>
                <p className="text-sm">{contactInfo?.phone || 'Not available'}</p>
              </div>
            </div>
            {contactInfo?.email && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Mail className="size-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm">{contactInfo.email}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleRevealContact}
            disabled={revealingContact}
            className="w-full py-3 bg-gradient-to-r from-rose-500 to-amber-400 hover:from-rose-600 hover:to-amber-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Lock className="size-5" />
            {revealingContact ? 'Revealing...' : 'Reveal Contact Details'}
          </button>
        )}
      </div>

      {/* Similar Profiles - Mobile */}
      {similarProfiles.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3>Similar Profiles</h3>
            <button className="text-sm text-rose-500">View All</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {similarProfiles.slice(0, 6).map((prof: any, idx: number) => (
              <div
                key={idx}
                className="cursor-pointer"
                onClick={() => {
                  if (onViewProfile) {
                    // Use callback if available (for MobileApp context)
                    onViewProfile(prof);
                  } else {
                    // Fallback to URL navigation (for standalone /app/profile/:slug route)
                    const firstName = prof.userId?.firstName || prof.firstName || 'User';
                    const lastName = prof.userId?.lastName || prof.lastName || '';
                    const profileId = prof._id || prof.id;
                    const slug = `${firstName.toLowerCase()}-${lastName.toLowerCase()}-${profileId}`.replace(/\s+/g, '-');
                    window.location.href = `/app/profile/${slug}`;
                  }
                }}
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-1 bg-gray-100">
                  <ImageWithFallback
                    src={prof.photos?.[0] || prof.profilePhoto || `https://ui-avatars.com/api/?name=${prof.firstName}+${prof.lastName}&background=f43f5e&color=fff`}
                    alt={`${prof.firstName} ${prof.lastName}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs truncate">{prof.firstName}, {prof.age}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Desktop Profile Content Component
function DesktopProfileContent({ profile, photos, currentPhotoIndex, setCurrentPhotoIndex, contactRevealed, contactInfo, handleRevealContact, revealingContact, onNavigateToPlans, hideInterestButtons, handleLike, isLiking, receivedInteraction, handleAcceptInterest, acceptingInterest, declinedInteraction, onBack }: any) {
  return (
    <>
      {/* Photo Gallery */}
      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="relative aspect-[16/10] bg-gray-100">
          <ImageWithFallback
            src={photos[currentPhotoIndex]}
            alt={profile.name}
            className="w-full h-full object-cover"
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={() => setCurrentPhotoIndex((prev: number) => (prev === 0 ? photos.length - 1 : prev - 1))}
                className="absolute left-4 top-1/2 -translate-y-1/2 size-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="size-6" />
              </button>
              <button
                onClick={() => setCurrentPhotoIndex((prev: number) => (prev === photos.length - 1 ? 0 : prev + 1))}
                className="absolute right-4 top-1/2 -translate-y-1/2 size-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronRight className="size-6" />
              </button>
            </>
          )}
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
            <Eye className="size-4" />
            {currentPhotoIndex + 1}/{photos.length}
          </div>
          {profile.isOnline && (
            <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5">
              <div className="size-2 bg-white rounded-full animate-pulse"></div>
              Online Now
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {photos.length > 1 && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {photos.map((photo: string, idx: number) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  className={`flex-shrink-0 size-16 rounded-lg overflow-hidden border-2 transition-all ${currentPhotoIndex === idx ? 'border-rose-500 scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                >
                  <ImageWithFallback
                    src={photo}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl mb-1">{profile.name}, {profile.age}</h1>
            <p className="text-gray-600 flex items-center gap-2">
              <MapPin className="size-4" />
              {profile.location}
            </p>
          </div>
          {profile.isPro && (
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1">
              <Star className="size-3 fill-white" />
              Premium
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Briefcase className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Occupation</p>
              <p className="text-sm">{profile.occupation}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <GraduationCap className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Education</p>
              <p className="text-sm">{profile.education}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Shield className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Religion</p>
              <p className="text-sm">{profile.religion}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Users className="size-5 text-rose-500" />
            <div>
              <p className="text-xs text-gray-500">Marital Status</p>
              <p className="text-sm">{profile.maritalStatus || 'Never Married'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      {profile.about && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="mb-3">About</h3>
          <p className="text-gray-600 leading-relaxed">{profile.about}</p>
        </div>
      )}

      {/* Personal Details */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="mb-4">Personal Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow label="Height" value={profile.height} />
          <DetailRow label="Mother Tongue" value={profile.motherTongue || 'Not specified'} />
          <DetailRow label="Diet" value={profile.diet || 'Not specified'} />
          <DetailRow label="Smoking" value={profile.smoking || 'No'} />
          <DetailRow label="Drinking" value={profile.drinking || 'No'} />
          <DetailRow label="Community" value={profile.community || 'Not specified'} />
        </div>
      </div>

      {/* Family Background */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2">
          <Home className="size-5 text-rose-500" />
          Family Background
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow label="Family Type" value={profile.familyType || 'Not specified'} />
          <DetailRow label="Father's Occupation" value={profile.fatherOccupation || 'Not specified'} />
          <DetailRow label="Mother's Occupation" value={profile.motherOccupation || 'Not specified'} />
          <DetailRow label="Siblings" value={profile.siblings || 'Not specified'} />
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2">
          <Phone className="size-5 text-rose-500" />
          Contact Details
        </h3>
        {contactRevealed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <Phone className="size-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Phone Number</p>
                <p className="text-sm">{contactInfo?.phone || 'Not available'}</p>
              </div>
            </div>
            {contactInfo?.email && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Mail className="size-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm">{contactInfo.email}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleRevealContact}
            disabled={revealingContact}
            className="w-full py-3 bg-gradient-to-r from-rose-500 to-amber-400 hover:from-rose-600 hover:to-amber-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Lock className="size-5" />
            {revealingContact ? 'Revealing...' : 'Reveal Contact Details'}
          </button>
        )}
      </div>

      {/* Action Buttons */}
      {!hideInterestButtons && (
        <div className="sticky bottom-0 bg-white rounded-xl p-4 border border-gray-200 shadow-lg">
          <div className="flex gap-3">
            <button
              onClick={onBack}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <X className="size-5" />
              Skip
            </button>
            {declinedInteraction ? (
              <button
                disabled
                className="flex-[2] py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
              >
                <X className="size-5" />
                Interest Declined
              </button>
            ) : (
              <button
                onClick={receivedInteraction ? handleAcceptInterest : handleLike}
                disabled={receivedInteraction ? acceptingInterest : isLiking}
                className={`flex-[2] py-3 ${receivedInteraction ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-rose-500 to-amber-400 hover:from-rose-600 hover:to-amber-500'} text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {receivedInteraction ? (
                  <>
                    <Check className="size-5" />
                    {acceptingInterest ? 'Accepting...' : 'Accept Interest'}
                  </>
                ) : (
                  <>
                    <Heart className="size-5" />
                    {isLiking ? 'Sending...' : 'Send Interest'}
                  </>
                )}
              </button>
            )}
            <button className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
              <MessageCircle className="size-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Helper Components
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function QuickInfoItem({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="size-5 text-rose-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm truncate">{value}</p>
      </div>
    </div>
  );
}